import React from 'react';
import Icon from '../../../components/AppIcon';

const HRAnalytics = ({ hrProfiles, payrollRecords, attendanceData, leaveRequests, loading }) => {
  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return `$${parseFloat(amount)?.toFixed(2)}`;
  };

  // Calculate metrics
  const totalEmployees = hrProfiles?.length || 0;
  const activeEmployees = hrProfiles?.filter((p) => p?.employment_status === 'active')?.length || 0;
  const onLeaveEmployees = hrProfiles?.filter((p) => p?.employment_status === 'on_leave')?.length || 0;

  const totalPayroll = payrollRecords?.reduce((sum, r) => sum + (parseFloat(r?.gross_pay) || 0), 0);
  const avgPayroll = totalEmployees > 0 ? totalPayroll / totalEmployees : 0;

  const totalOvertimeHours = payrollRecords?.reduce((sum, r) => sum + (parseFloat(r?.overtime_hours) || 0), 0);
  const avgOvertimeHours = totalEmployees > 0 ? totalOvertimeHours / totalEmployees : 0;

  const pendingLeaveRequests = leaveRequests?.filter((r) => r?.status === 'pending')?.length || 0;
  const approvedLeaveRequests = leaveRequests?.filter((r) => r?.status === 'approved')?.length || 0;

  const totalPTOBalance = hrProfiles?.reduce((sum, p) => sum + (parseFloat(p?.pto_balance_hours) || 0), 0);
  const totalSickLeaveBalance = hrProfiles?.reduce((sum, p) => sum + (parseFloat(p?.sick_leave_balance_hours) || 0), 0);

  // Department breakdown
  const departmentCounts = hrProfiles?.reduce((acc, profile) => {
    const dept = profile?.department || 'Unassigned';
    acc[dept] = (acc?.[dept] || 0) + 1;
    return acc;
  }, {});

  const departments = Object.entries(departmentCounts || {})?.map(([name, count]) => ({ name, count }));

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 rounded-lg bg-primary/10">
              <Icon name="Users" size={24} className="text-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{totalEmployees}</div>
              <div className="text-xs text-muted-foreground">Total Employees</div>
            </div>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <span className="text-success">{activeEmployees} Active</span>
            <span className="text-warning">{onLeaveEmployees} On Leave</span>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-success/10">
              <Icon name="DollarSign" size={24} className="text-success" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{formatCurrency(totalPayroll)}</div>
              <div className="text-xs text-muted-foreground">Total Payroll</div>
            </div>
          </div>
          <div className="mt-2 text-xs text-muted-foreground">
            Avg: {formatCurrency(avgPayroll)} per employee
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-warning/10">
              <Icon name="Clock" size={24} className="text-warning" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{totalOvertimeHours?.toFixed(1)}h</div>
              <div className="text-xs text-muted-foreground">Total Overtime</div>
            </div>
          </div>
          <div className="mt-2 text-xs text-muted-foreground">
            Avg: {avgOvertimeHours?.toFixed(1)}h per employee
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-primary/10">
              <Icon name="Calendar" size={24} className="text-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{pendingLeaveRequests}</div>
              <div className="text-xs text-muted-foreground">Pending Leave</div>
            </div>
          </div>
          <div className="mt-2 text-xs text-muted-foreground">
            {approvedLeaveRequests} approved this period
          </div>
        </div>
      </div>
      {/* Department Breakdown */}
      <div className="card">
        <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Building" size={20} className="text-primary" />
          Department Breakdown
        </h3>
        {departments?.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">No department data available</p>
        ) : (
          <div className="space-y-3">
            {departments?.map((dept) => (
              <div key={dept?.name} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                <span className="font-medium text-foreground">{dept?.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">{dept?.count} employees</span>
                  <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary"
                      style={{ width: `${(dept?.count / totalEmployees) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Time Off Balances */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Palmtree" size={20} className="text-primary" />
            PTO Balance
          </h3>
          <div className="space-y-4">
            <div className="p-4 bg-primary/10 rounded-lg">
              <div className="text-3xl font-bold text-primary mb-1">{totalPTOBalance?.toFixed(0)}h</div>
              <div className="text-sm text-muted-foreground">Total PTO Balance</div>
            </div>
            <div className="text-sm text-muted-foreground">
              Average: {(totalPTOBalance / (totalEmployees || 1))?.toFixed(1)}h per employee
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Heart" size={20} className="text-primary" />
            Sick Leave Balance
          </h3>
          <div className="space-y-4">
            <div className="p-4 bg-primary/10 rounded-lg">
              <div className="text-3xl font-bold text-primary mb-1">{totalSickLeaveBalance?.toFixed(0)}h</div>
              <div className="text-sm text-muted-foreground">Total Sick Leave Balance</div>
            </div>
            <div className="text-sm text-muted-foreground">
              Average: {(totalSickLeaveBalance / (totalEmployees || 1))?.toFixed(1)}h per employee
            </div>
          </div>
        </div>
      </div>
      {/* Employment Status Distribution */}
      <div className="card">
        <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="PieChart" size={20} className="text-primary" />
          Employment Status Distribution
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { status: 'active', label: 'Active', color: 'success' },
            { status: 'on_leave', label: 'On Leave', color: 'warning' },
            { status: 'probation', label: 'Probation', color: 'warning' },
            { status: 'suspended', label: 'Suspended', color: 'error' },
            { status: 'terminated', label: 'Terminated', color: 'muted' }
          ]?.map((item) => {
            const count = hrProfiles?.filter((p) => p?.employment_status === item?.status)?.length || 0;
            return (
              <div key={item?.status} className="p-4 rounded-lg bg-muted/30 text-center">
                <div className={`text-2xl font-bold text-${item?.color} mb-1`}>{count}</div>
                <div className="text-xs text-muted-foreground">{item?.label}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default HRAnalytics;